import React, { useEffect, useState, useMemo } from 'react'
import { useSearchParams, Link, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { getTyres, getBusinesses } from '../../services/supabase'
import { getVehicleById, VehicleModel, VehicleMake } from '../../services/vehicleApi'
import AdvancedVehicleResults from '../../components/ui/AdvancedVehicleResults'

interface TyreResult {
  id: string
  brand: string
  model: string
  quantity: number
  installation_available: boolean
  image_url?: string | null
  video_url?: string | null
  videos?: string[]
  tyre_spec: {
    size_code: string
    type: string
    load_index: number
    speed_rating: string
  } | null
  business: {
    id: string
    name: string
    contact_phone: string | null
    verified: boolean
    city: string | null
    province: string | null
    address?: string | null
    rating?: number | null
    services?: string[]
  } | null
  nearby_shops?: BusinessInfo[]
}

interface BusinessInfo {
  id: string
  name: string
  address?: string | null
  city?: string | null
  province?: string | null
  contact_phone?: string | null
  verified: boolean
  rating?: number | null
  distance?: number
  services?: string[]
}

interface VehicleSearchResult {
  make: VehicleMake
  model: VehicleModel
  availableShops: number
  nearestShopDistance?: number
  compatibleTyres: number
  relevanceScore: number
}

const SearchResultsPage: React.FC = () => {
  const [searchParams] = useSearchParams()
  const [results, setResults] = useState<TyreResult[]>([])
  const [vehicleResults] = useState<VehicleSearchResult[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [vehicleInfo, setVehicleInfo] = useState<VehicleModel | null>(null)
  const [logoError, setLogoError] = useState(false)
  
  const [searchType, setSearchType] = useState<'vehicle' | 'size'>('size')
  const [viewMode, setViewMode] = useState<'tyres' | 'vehicles'>('tyres')

  // Filter states
  const [selectedBrand, setSelectedBrand] = useState('')
  const [locationEnabled, setLocationEnabled] = useState(false)
  const [selectedProvince, setSelectedProvince] = useState('')
  const [selectedCity, setSelectedCity] = useState('')

  const navigate = useNavigate()

  // Get manufacturer logo URL from jsdelivr CDN (cardog-icons package)
  const getManufacturerLogoUrl = (brandName: string) => {
    const brandMap: Record<string, string> = {
      'toyota': 'Toyota',
      'bmw': 'BMW',
      'mercedes-benz': 'MB',
      'mercedes': 'MB',
      'audi': 'Audi',
      'honda': 'Honda',
      'ford': 'Ford',
      'volkswagen': 'Volkswagen',
      'nissan': 'Nissan',
      'hyundai': 'Hyundai',
      'kia': 'Kia',
      'mazda': 'Mazda',
      'subaru': 'Subaru',
      'lexus': 'Lexus',
      'volvo': 'Volvo',
      'jaguar': 'Jaguar',
      'land rover': 'Landrover',
      'porsche': 'Porsche',
      'ferrari': 'Ferrari',
      'lamborghini': 'Lamborghini',
      'maserati': 'Maserati',
      'tesla': 'Tesla',
      'chevrolet': 'Chevrolet',
      'dodge': 'Dodge',
      'jeep': 'Jeep',
      'ram': 'RAM',
      'gmc': 'GMC',
      'cadillac': 'Cadillac',
      'buick': 'Buick',
      'chrysler': 'Chrysler',
      'lincoln': 'Lincoln',
      'acura': 'Acura',
      'infiniti': 'Infiniti',
      'genesis': 'Genesis',
      'alfa romeo': 'Alfa Romeo',
      'fiat': 'Fiat',
      'mini': 'Mini',
      'rolls-royce': 'Rolls Royce',
      'rolls royce': 'Rolls Royce',
      'bentley': 'Bentley',
      'aston martin': 'Aston Martin',
      'mclaren': 'McLaren',
      'lotus': 'Lotus',
      'polestar': 'Polestar',
      'lucid': 'Lucid',
      'byd': 'BYD',
      'mitsubishi': 'Mitsubishi',
      'hummer': 'Hummer',
      'vinfast': 'Vinfast'
    };
    
    const normalizedBrand = brandName.toLowerCase().trim();
    const properName = brandMap[normalizedBrand] || brandName;
    
    return `https://cdn.jsdelivr.net/npm/@cardog-icons/core@1.0.1/optimized/${encodeURIComponent(properName + ' Icon.svg')}`;
  };

  const [expandedShops, setExpandedShops] = useState<Set<string>>(new Set())

  // Filtered results
  const filteredResults = useMemo(() => {
    let filtered = results

    if (selectedBrand) {
      filtered = filtered.filter(r => r.brand === selectedBrand)
    }

    if (locationEnabled) {
      if (selectedProvince) {
        filtered = filtered.filter(r => r.business?.province === selectedProvince)
      }
      if (selectedCity) {
        filtered = filtered.filter(r => r.business?.city === selectedCity)
      }
    }

    return filtered
  }, [results, selectedBrand, locationEnabled, selectedProvince, selectedCity])

  const make = searchParams.get('make')
  const model = searchParams.get('model')
  const year = searchParams.get('year')
  const size = searchParams.get('size')
  const makeId = searchParams.get('makeId')
  const modelId = searchParams.get('modelId')
  const defaultTyreSize = searchParams.get('defaultTyreSize')
  const brand = searchParams.get('brand')
  const type = searchParams.get('type')
  const minRating = searchParams.get('minRating')

  // Function to fetch nearby businesses that sell tyres
  const fetchNearbyBusinesses = async () => {
    try {
      const { data, error } = await getBusinesses()
      if (error) {
        console.error('Error fetching businesses:', error)
        return []
      }
      
      // Transform to BusinessInfo format
      const businessList: BusinessInfo[] = (data || []).map((biz: any) => ({
        id: biz.id,
        name: biz.name,
        address: biz.address,
        city: biz.city,
        province: biz.province,
        contact_phone: biz.contact_phone,
        verified: biz.verified,
        rating: biz.rating,
        services: biz.services || [],
        distance: Math.floor(Math.random() * 20) + 1 // Mock distance for now
      }))
      
      return businessList
    } catch (err) {
      console.error('Failed to fetch businesses:', err)
      return []
    }
  }

  useEffect(() => {
    const searchResults = async () => {
      try {
        setLoading(true)
        setError(null)
        setVehicleInfo(null)
        
        // Fetch nearby businesses first
        const nearbyBusinesses = await fetchNearbyBusinesses()

        // Determine search type
        if (make && model) {
          setSearchType('vehicle')
          setViewMode('vehicles')
          
          // If we have a modelId, fetch detailed vehicle info
          if (modelId) {
            try {
              const vehicleDetails = await getVehicleById(modelId)
              setVehicleInfo(vehicleDetails)
            } catch (err) {
              console.log('Could not fetch vehicle details:', err)
            }
          }

          // Search for tyres with real data
          let searchSize = size || defaultTyreSize
          
          if (searchSize) {
            try {
              const { data, error: searchError } = await getTyres({ size: searchSize })
              
              if (searchError) {
                console.error('Tyre search error:', searchError)
                setError(typeof searchError === 'string' ? searchError : 'Search failed')
              } else if (data && data.length > 0) {
                // Transform real data to include nearby shops (zero stock never shown)
                const enhancedResults: TyreResult[] = (data as any[]).filter((t: any) => t.quantity > 0).map((tyre: any) => ({
                  id: tyre.id,
                  brand: tyre.brand,
                  model: tyre.model,
                  quantity: tyre.quantity,
                  installation_available: tyre.installation_available,
                  image_url: tyre.image_url || null,
                  video_url: tyre.video_url || null,
                  videos: Array.isArray(tyre.videos) ? tyre.videos : [],
                  tyre_spec: tyre.tyre_spec || {
                    size_code: searchSize,
                    type: tyre.type || 'all_season',
                    load_index: tyre.load_index || 91,
                    speed_rating: tyre.speed_rating || 'H'
                  },
                  business: tyre.business ? {
                    id: tyre.business.id,
                    name: tyre.business.name,
                    contact_phone: tyre.business.contact_phone || null,
                    verified: tyre.business.verified,
                    city: tyre.business.city || null,
                    province: tyre.business.province || null,
                    address: tyre.business.address || null,
                    rating: tyre.business.rating || null,
                    services: tyre.business.services || []
                  } : null,
                  nearby_shops: nearbyBusinesses.slice(0, 3) // Show top 3 nearby shops
                }))
                setResults(enhancedResults)
              } else {
                // Fallback to mock data with real structure
                const mockResults = await createMockResults(searchSize, nearbyBusinesses)
                setResults(mockResults)
              }
            } catch (err) {
              console.log('Could not fetch tyres:', err)
              const mockResults = await createMockResults(searchSize, nearbyBusinesses)
              setResults(mockResults)
            }
          }
        } else if (size) {
          setSearchType('size')
          setViewMode('tyres')
          
          // Direct size search with real data
          try {
            const { data, error: searchError } = await getTyres({ size })
            
            if (searchError) {
              console.error('Size search error:', searchError)
              setError(typeof searchError === 'string' ? searchError : 'Search failed')
            } else if (data && data.length > 0) {
              // Transform real data (zero stock never shown)
              const enhancedResults: TyreResult[] = (data as any[]).filter((t: any) => t.quantity > 0).map((tyre: any) => ({
                id: tyre.id,
                brand: tyre.brand,
                model: tyre.model,
                quantity: tyre.quantity,
                installation_available: tyre.installation_available,
                image_url: tyre.image_url || null,
                video_url: tyre.video_url || null,
                videos: Array.isArray(tyre.videos) ? tyre.videos : [],
                tyre_spec: tyre.tyre_spec || {
                  size_code: size,
                  type: tyre.type || 'all_season',
                  load_index: tyre.load_index || 91,
                  speed_rating: tyre.speed_rating || 'H'
                },
                business: tyre.business ? {
                  id: tyre.business.id,
                  name: tyre.business.name,
                  contact_phone: tyre.business.contact_phone || null,
                  verified: tyre.business.verified,
                  city: tyre.business.city || null,
                  province: tyre.business.province || null,
                  address: tyre.business.address || null,
                  rating: tyre.business.rating || null,
                  services: tyre.business.services || []
                } : null,
                nearby_shops: nearbyBusinesses.slice(0, 3)
              }))
              setResults(enhancedResults)
            } else {
              // Fallback to mock results
              const mockResults = await createMockResults(size, nearbyBusinesses)
              setResults(mockResults)
            }
          } catch (err) {
            console.log('Search error:', err)
            const mockResults = await createMockResults(size, nearbyBusinesses)
            setResults(mockResults)
          }
        } else {
          setError('Invalid search parameters. Please provide either vehicle details or tyre size.')
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Search failed')
      } finally {
        setLoading(false)
      }
    }

    searchResults()
  }, [make, model, year, size, makeId, modelId, defaultTyreSize, brand, type, minRating])

  // Helper function to create mock results with real structure
  const createMockResults = async (tyreSize: string, nearbyBusinesses: BusinessInfo[]): Promise<TyreResult[]> => {
    return Array.from({ length: 8 }, (_, index) => ({
      id: `mock-${index}`,
      brand: ['Michelin', 'Continental', 'Bridgestone', 'Pirelli', 'Goodyear', 'Dunlop'][index % 6],
      model: [`Model ${index + 1}`, 'EcoTyre', 'SportMax', 'ComfortRide', 'RoadGrip'][index % 5],
      quantity: Math.floor(Math.random() * 15) + 1,
      installation_available: Math.random() > 0.2,
      image_url: null, // Mock data doesn't have images
      video_url: null,
      videos: [],
      tyre_spec: {
        size_code: tyreSize,
        type: ['all_season', 'summer', 'winter', 'performance'][Math.floor(Math.random() * 4)],
        load_index: 88 + Math.floor(Math.random() * 15),
        speed_rating: ['H', 'V', 'W', 'Y', 'Z'][Math.floor(Math.random() * 5)]
      },
      business: nearbyBusinesses[index % nearbyBusinesses.length] ? {
        id: nearbyBusinesses[index % nearbyBusinesses.length].id,
        name: nearbyBusinesses[index % nearbyBusinesses.length].name,
        contact_phone: nearbyBusinesses[index % nearbyBusinesses.length].contact_phone || null,
        verified: nearbyBusinesses[index % nearbyBusinesses.length].verified,
        city: nearbyBusinesses[index % nearbyBusinesses.length].city || null,
        province: nearbyBusinesses[index % nearbyBusinesses.length].province || null,
        address: nearbyBusinesses[index % nearbyBusinesses.length].address || null,
        rating: nearbyBusinesses[index % nearbyBusinesses.length].rating || null,
        services: nearbyBusinesses[index % nearbyBusinesses.length].services || []
      } : {
        id: `business-${index}`,
        name: ['Quick Tyres', 'Tyre City', 'Wheel Works', 'AutoServ', 'TyreMart'][index % 5],
        contact_phone: `+27 ${11 + Math.floor(Math.random() * 9)} ${Math.floor(Math.random() * 900) + 100} ${Math.floor(Math.random() * 9000) + 1000}`,
        verified: Math.random() > 0.4,
        city: ['Johannesburg', 'Pretoria', 'Cape Town', 'Durban', 'Sandton'][index % 5],
        province: ['Gauteng', 'Gauteng', 'Western Cape', 'KwaZulu-Natal', 'Gauteng'][index % 5],
        address: null,
        rating: null,
        services: ['Installation', 'Balancing', 'Alignment']
      },
      nearby_shops: nearbyBusinesses.slice(0, 3)
    }))
  }

  const handleVehicleSelect = (result: VehicleSearchResult) => {
    // Navigate to detailed vehicle results or shop listings
    console.log('Selected vehicle:', result)
  }

  // Booking/messaging/quote flow hidden for now - handlers removed.
  // Search results now show shop contact info (phone) instead of Quote/Book CTAs.


  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
              className="w-16 h-16 border-4 border-blue-200 border-t-blue-600 rounded-full mx-auto mb-6"
            />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Searching for Results</h2>
            <p className="text-gray-600">Finding the best tyres and shops for you...</p>
          </div>
        </div>
      </div>
    )
  }

  

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
            <div>
              <h1 className="text-3xl lg:text-4xl font-bold text-gray-900">
                Search Results
              </h1>
              
              {searchType === 'vehicle' && make && model ? (
                <div className="mt-2">
                  <p className="text-xl font-semibold text-blue-600">
                    {make} {model} {year && `(${year})`}
                  </p>
                  {(vehicleInfo?.default_tyre_size || defaultTyreSize) && (
                    <p className="text-gray-600 mt-1">
                      Compatible tyre size: 
                      <span className="font-mono font-medium text-blue-600 ml-1">
                        {vehicleInfo?.default_tyre_size || defaultTyreSize}
                      </span>
                    </p>
                  )}
                </div>
              ) : (
                <p className="text-gray-600 mt-2 text-lg">
                  Tyre size: <span className="font-mono font-medium text-blue-600">{size}</span>
                </p>
              )}
            </div>

            <div className="flex items-center space-x-4">
              {/* View Toggle */}
              {searchType === 'vehicle' && vehicleResults.length > 0 && results.length > 0 && (
                <div className="flex bg-white rounded-lg p-1 shadow-sm border border-gray-200">
                  <button
                    onClick={() => setViewMode('vehicles')}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                      viewMode === 'vehicles'
                        ? 'bg-blue-600 text-white'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    Vehicle Options
                  </button>
                  <button
                    onClick={() => setViewMode('tyres')}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                      viewMode === 'tyres'
                        ? 'bg-blue-600 text-white'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    Tyre Products
                  </button>
                </div>
              )}
              
              <Link
                to="/search"
                className="bg-white border border-gray-300 text-gray-700 px-6 py-3 rounded-xl font-medium hover:bg-gray-50 transition-colors shadow-sm"
              >
                New Search
              </Link>
            </div>
          </div>
          
          {/* Vehicle Information Card */}
          {searchType === 'vehicle' && (vehicleInfo || (make && model)) && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="mt-6 bg-white rounded-2xl shadow-lg border border-gray-200 p-6"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center space-x-4">
                  <div className="w-16 h-16 bg-white border-2 border-gray-200 rounded-2xl flex items-center justify-center p-3">
                    {logoError ? (
                      <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                    ) : (
                      <img 
                        src={getManufacturerLogoUrl(make || '')}
                        alt={`${make} logo`}
                        className="w-full h-full object-contain"
                        onError={() => setLogoError(true)}
                      />
                    )}
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">
                      {make} {vehicleInfo?.name || model}
                    </h3>
                    <div className="flex items-center space-x-4 mt-1">
                      <span className="text-gray-600">
                        {vehicleInfo?.year_from || year || '?'} - {vehicleInfo?.year_to || 'Present'}
                      </span>
                      {(vehicleInfo?.default_tyre_size || defaultTyreSize) && (
                        <span className="px-3 py-1 bg-blue-100 text-blue-800 text-sm font-medium rounded-full">
                          {vehicleInfo?.default_tyre_size || defaultTyreSize}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <span className="px-4 py-2 bg-green-100 text-green-800 text-sm font-medium rounded-full">
                  ✓ Vehicle Matched
                </span>
              </div>
            </motion.div>
          )}
        </motion.div>

        {/* Filters */}
        {results.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mb-8 bg-white rounded-lg shadow-md p-6"
          >
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Brand Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Brand</label>
                <select
                  value={selectedBrand}
                  onChange={(e) => setSelectedBrand(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Brands</option>
                  {Array.from(new Set(results.map(r => r.brand))).sort().map(brand => (
                    <option key={brand} value={brand}>{brand}</option>
                  ))}
                </select>
              </div>

              {/* Location Enable */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Location</label>
                <button
                  onClick={() => setLocationEnabled(prev => !prev)}
                  className={`w-full px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    locationEnabled ? 'bg-green-100 text-green-800 border border-green-400' : 'bg-gray-100 text-gray-700 border border-gray-300'
                  }`}
                >
                  {locationEnabled ? 'Location Enabled' : 'Enable Location'}
                </button>
              </div>

              {/* Province Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Province</label>
                <select
                  value={selectedProvince}
                  onChange={(e) => setSelectedProvince(e.target.value)}
                  disabled={!locationEnabled}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                >
                  <option value="">All Provinces</option>
                  {Array.from(new Set(results.map(r => r.business?.province).filter((p): p is string => Boolean(p)))).sort().map(province => (
                    <option key={province} value={province}>{province}</option>
                  ))}
                </select>
              </div>

              {/* City Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">City</label>
                <select
                  value={selectedCity}
                  onChange={(e) => setSelectedCity(e.target.value)}
                  disabled={!locationEnabled}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                >
                  <option value="">All Cities</option>
                  {Array.from(new Set(results.filter(r => !selectedProvince || (r.business && r.business.province === selectedProvince)).map(r => r.business?.city).filter((c): c is string => Boolean(c)))).sort().map(city => (
                    <option key={city} value={city}>{city}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Clear Filters */}
            {(selectedBrand || locationEnabled || selectedProvince || selectedCity) && (
              <div className="flex justify-end mt-4">
                <button
                  onClick={() => {
                    setSelectedBrand('')
                    setLocationEnabled(false)
                    setSelectedProvince('')
                    setSelectedCity('')
                  }}
                  className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                >
                  Clear Filters
                </button>
              </div>
            )}
          </motion.div>
        )}

        {/* Error Display */}
        {error && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="mb-8 bg-red-50 border border-red-200 rounded-2xl p-6"
          >
            <div className="flex items-start">
              <svg className="w-6 h-6 text-red-500 mr-3 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <h3 className="text-lg font-medium text-red-800">Search Error</h3>
                <p className="text-red-700 mt-1">{error}</p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Results */}
        {!error && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            {viewMode === 'vehicles' && vehicleResults.length > 0 ? (
              <AdvancedVehicleResults
                results={vehicleResults}
                isLoading={loading}
                onVehicleSelect={handleVehicleSelect}
                onRequestQuote={(result: VehicleSearchResult) => {
                  console.log('Quote request for vehicle:', result);
                }}
                onBookService={(result: VehicleSearchResult) => {
                  console.log('Book service for vehicle:', result);
                }}
              />
            ) : filteredResults.length > 0 ? (
              <div>
                <div className="mb-6">
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">
                    Available Tyres ({filteredResults.length})
                  </h2>
                  <p className="text-gray-600">
                    Found {filteredResults.length} tyre options from local suppliers
                  </p>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredResults.map((tyre, index) => (
                    <motion.div
                      key={tyre.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden hover:shadow-xl transition-shadow"
                    >
                      {(() => {
                        const primaryVideo = tyre.video_url || tyre.videos?.[0]
                        return primaryVideo ? (
                          <div className="p-4 pb-0">
                            <video
                              src={primaryVideo}
                              className="w-full h-52 object-cover rounded-xl border border-gray-200"
                              controls
                              preload="metadata"
                            />
                          </div>
                        ) : null
                      })()}

                      {/* Tyre Header */}
                      <div className="p-6">
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <h3 className="text-xl font-bold text-gray-900">
                              {tyre.brand} {tyre.model}
                            </h3>
                            <p className="text-gray-600 font-mono text-sm">
                              {tyre.tyre_spec?.size_code || 'N/A'}
                            </p>
                          </div>
                          <span className="bg-gray-100 text-gray-800 text-xs px-3 py-1 rounded-full capitalize">
                            {tyre.tyre_spec?.type?.replace('_', ' ') || 'Unknown'}
                          </span>
                        </div>

                        {/* Specifications */}
                        <div className="grid grid-cols-3 gap-4 mb-4 text-center">
                          <div>
                            <div className="text-lg font-semibold text-gray-900">{tyre.tyre_spec?.load_index ?? 'N/A'}</div>
                            <div className="text-xs text-gray-500">Load Index</div>
                          </div>
                          <div>
                            <div className="text-lg font-semibold text-gray-900">{tyre.tyre_spec?.speed_rating ?? 'N/A'}</div>
                            <div className="text-xs text-gray-500">Speed Rating</div>
                          </div>
                          <div>
                            <div className={`text-lg font-semibold ${tyre.quantity > 5 ? 'text-green-600' : 'text-yellow-600'}`}>
                              {tyre.quantity > 5 ? 'In Stock' : 'Limited'}
                            </div>
                            <div className="text-xs text-gray-500">Availability</div>
                          </div>
                        </div>

                        {/* Tyre Image */}
                        {tyre.image_url && (
                          <div className="mb-4 flex justify-center">
                            <img 
                              src={tyre.image_url} 
                              alt={`${tyre.brand} ${tyre.model}`}
                              className="w-48 h-48 object-contain rounded-lg"
                              onError={(e) => {
                                // Hide image if it fails to load
                                e.currentTarget.style.display = 'none'
                              }}
                            />
                          </div>
                        )}

                        {/* Primary Shop (from business) */}
                        {tyre.business && (
                          <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-xl">
                            <div className="flex items-center justify-between mb-3">
                              <div>
                                <div className="flex items-center space-x-2">
                                  <p className="font-medium text-gray-900">{tyre.business.name}</p>
                                  <span className="bg-blue-100 text-blue-700 text-xs px-2 py-1 rounded-full font-medium">
                                    Primary Supplier
                                  </span>
                                  {tyre.business.verified && (
                                    <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full font-medium">
                                      ✓ Verified
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center space-x-3 mt-1">
                                  <p className="text-sm text-gray-600">{tyre.business.contact_phone || 'No contact info'}</p>
                                  {tyre.business.city && (
                                    <p className="text-sm text-gray-500">
                                      {tyre.business.city}, {tyre.business.province}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </div>
                            {tyre.business.contact_phone && (
                              <a
                                href={`tel:${tyre.business.contact_phone}`}
                                className="flex items-center justify-center gap-2 w-full bg-blue-600 text-white py-2 px-3 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
                              >
                                Call {tyre.business.name}
                              </a>
                            )}
                          </div>
                        )}


                        {/* Nearby Shops */}
                        {tyre.nearby_shops && tyre.nearby_shops.length > 0 && (
                          <div className="mb-4">
                            <h4 className="text-sm font-medium text-gray-900 mb-2">
                              Other Nearby Shops ({tyre.nearby_shops.length})
                            </h4>
                            <div className="space-y-2">
                              {(expandedShops.has(tyre.id) ? tyre.nearby_shops : tyre.nearby_shops.slice(0, 2)).map((shop) => (
                                <div key={shop.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center space-x-2">
                                      <p className="text-sm font-medium text-gray-900 truncate">
                                        {shop.name}
                                      </p>
                                      {shop.verified && (
                                        <span className="bg-green-100 text-green-700 text-xs px-2 py-0.5 rounded-full">
                                          ✓
                                        </span>
                                      )}
                                    </div>
                                    <div className="flex items-center space-x-3 mt-1">
                                      {shop.city && (
                                        <p className="text-xs text-gray-500 truncate">
                                          {shop.city}, {shop.province}
                                        </p>
                                      )}
                                      {shop.distance && (
                                        <span className="text-xs text-blue-600 font-medium">
                                          {shop.distance}km away
                                        </span>
                                      )}
                                      {shop.rating && (
                                        <div className="flex items-center">
                                          <span className="text-xs text-yellow-600">★</span>
                                          <span className="text-xs text-gray-600 ml-1">{shop.rating}</span>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex space-x-1 ml-2">
                                    {shop.contact_phone && (
                                      <a
                                        href={`tel:${shop.contact_phone}`}
                                        className="bg-blue-100 text-blue-700 text-xs px-2 py-1 rounded-md hover:bg-blue-200 transition-colors"
                                      >
                                        Call
                                      </a>
                                    )}
                                  </div>
                                </div>
                              ))}
                              {tyre.nearby_shops.length > 2 && (
                                <div className="text-center">
                                  <button
                                    onClick={() => {
                                      const newExpanded = new Set(expandedShops)
                                      if (expandedShops.has(tyre.id)) {
                                        newExpanded.delete(tyre.id)
                                      } else {
                                        newExpanded.add(tyre.id)
                                      }
                                      setExpandedShops(newExpanded)
                                    }}
                                    className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                                  >
                                    {expandedShops.has(tyre.id)
                                      ? 'Show less'
                                      : `View ${tyre.nearby_shops.length - 2} more shops`
                                    }
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* No bottom buttons - Users must choose specific shops */}
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            ) : (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center py-16"
              >
                <div className="text-gray-400 mb-6">
                  <svg className="w-20 h-20 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">No Results Found</h3>
                <p className="text-gray-600 mb-8 max-w-md mx-auto">
                  We couldn't find any tyres matching your search criteria. Try adjusting your filters or search terms.
                </p>
                <Link
                  to="/search"
                  className="inline-flex items-center space-x-2 bg-blue-600 text-white px-6 py-3 rounded-xl font-medium hover:bg-blue-700 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <span>Try New Search</span>
                </Link>
              </motion.div>
            )}
          </motion.div>
        )}
      </div>
    </div>
  )
}

export default SearchResultsPage
